// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
//
// Branch length label renderer for the SVG phylogenetic tree viewer.
//
// Implementation notes:
//
//   WHICH NODES — iterates firstIndex+1 (skipping root) through lastIndex,
//   including leaves.  Hidden nodes are skipped here for visual correctness
//   (they have no positioned coordinates).
//
//   POSITION — branch midpoint is computed directly from positioned pixel
//   coordinates.  For Circular trees the radial segment runs node → (cx, cy),
//   so the midpoint is [(node.x + node.cx)/2, (node.y + node.cy)/2].
//
//   PIXEL OFFSET — perpendicular offset formula:
//     offsetY = fontSize / 3
//     dx = (inverted ? -1 : 1) * offsetY * sin(angle)
//     dy = (inverted ? 1 : -1) * offsetY * cos(angle)
//   This lifts the label off the branch line on the appropriate side.
//
//   ROTATION — SVG rotate() is degrees clockwise from positive-X axis.
//   Formula (consistent with labels.js):
//     degrees = (node.angle / Angles.Degrees360) * 360 + (node.inverted ? 180 : 0)
//
//   TEXT ANCHOR — "middle".

import { el } from "./dom.js";
import { Angles, TreeTypes } from "../core/constants.js";

/**
 * Format a branch-length value according to the requested format.
 *
 * Returns undefined when branchLength is null/undefined (caller should skip).
 *
 * @param {number|null|undefined} branchLength
 * @param {string|null} format  - "decimal" | "scientific" | falsy (toString)
 * @param {number} digits       - precision digits for decimal/scientific formats
 * @returns {string|undefined}
 */
function formatBranchLength(branchLength, format, digits) {
  if (format === "decimal") {
    return branchLength?.toFixed(digits);
  }
  if (format === "scientific") {
    return branchLength?.toExponential(digits);
  }
  return branchLength?.toString();
}

/**
 * Compute the midpoint position of the branch segment leading to `node`.
 *
 * Uses pixel coordinates already baked into node.x/y by scaleGraph, which
 * avoids needing branchScale.  For Circular trees the radial segment runs
 * from (node.x, node.y) to (node.cx, node.cy).
 *
 * @param {object} node  - A non-root node with parent and pixel coordinates.
 * @param {string} type  - One of the TreeTypes values.
 * @returns {[number, number]}
 */
function branchMidpoint(node, type) {
  if (type === TreeTypes.Rectangular) {
    return [(node.x + node.parent.x) / 2, node.y];
  }
  if (type === TreeTypes.Hierarchical) {
    return [node.x, (node.y + node.parent.y) / 2];
  }
  if (type === TreeTypes.Circular) {
    // Radial segment runs node -> (cx, cy); cx/cy is the arc-connection point.
    return [(node.x + node.cx) / 2, (node.y + node.cy) / 2];
  }
  // Diagonal and Radial: straight line node -> parent.
  return [(node.x + node.parent.x) / 2, (node.y + node.parent.y) / 2];
}

/**
 * Render branch-length labels as an SVG <g> containing one <text> per
 * non-root node whose branch length can be formatted to a non-empty string.
 *
 * Returns null when branch lengths are disabled.
 *
 * @param {object} graph - Built graph returned by buildGraph().
 * @param {object} props - Merged rendering props.
 * @returns {SVGGElement|null}
 */
export function renderBranchLabels(graph, props) {
  if (!props.showBranchLengths) {
    return null;
  }

  const fontSize = props.branchLabelsFontSize ?? (props.fontSize ?? 16) * 0.6;
  const fontFamily = (props.fontFamily ?? "monospace").replace(/"/g, "'");

  const format = props.branchLengthsFormat ?? null;
  const digits = props.branchLengthsDigits ?? 4;
  const treeType = props.type;

  const textElements = [];

  for (let i = graph.firstIndex + 1; i < graph.lastIndex; i++) {
    const node = graph.preorderTraversal[i];

    if (node.isCollapsed) {
      i += node.totalNodes - 1;
      // Still process the collapsed node itself (its branch label is visible).
    }

    if (node.isHidden || !node.parent) {
      continue;
    }

    const text = formatBranchLength(node.branchLength, format, digits);
    if (text == null) {
      continue;
    }

    const [midX, midY] = branchMidpoint(node, treeType);

    // Perpendicular pixel offset to lift the label off the branch line.
    const offsetY = fontSize / 3;
    const dx = (node.inverted ? -1 : 1) * offsetY * Math.sin(node.angle);
    const dy = (node.inverted ? 1 : -1) * offsetY * Math.cos(node.angle);

    const x = midX + dx;
    const y = midY + dy;

    // SVG-convention rotation formula (consistent with labels.js).
    const degrees =
      (node.angle / Angles.Degrees360) * 360 + (node.inverted ? 180 : 0);

    textElements.push(
      el(
        "text",
        {
          x: x,
          y: y,
          "text-anchor": "middle",
          "dominant-baseline": "middle",
          transform: `rotate(${degrees},${x},${y})`,
          "data-node-id": node.id,
        },
        [text],
      ),
    );
  }

  return el(
    "g",
    {
      class: "branch-labels",
      "font-family": fontFamily,
      "font-size": fontSize,
    },
    textElements,
  );
}
